import { Link } from '@tanstack/react-router'
import { CheckCircle2, HelpCircle, ShieldCheck, UserCheck } from 'lucide-react'

import type { DualControlResult } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

/**
 * Dual control on the loan screens, which comes in two shapes rather than one.
 *
 * **The shape that answers plainly.** Approval, disbursement, product changes and putting a
 * classification scheme into force answer with a {@link DualControlResult}. `awaitingApproval`
 * says exactly what happened, and {@link DualControlOutcome} reports it.
 *
 * **The shape that does not.** The penalty waiver, the restructure, the write-off, the guarantee
 * offset and the classification override are countersigned too — but they answer with the round,
 * and when the branch's policy defers them the round comes back *unchanged*. There is no member
 * on that response that says "queued". A screen that treated a 200 as success there would tell an
 * officer a member's penalty had been forgiven while the request was still sitting in a queue.
 *
 * So for those, {@link CountersignedOutcome} reports what can actually be established: the figure
 * the action would have moved, before and after. If it moved, the action took effect. If it did
 * not, either it is waiting for a countersignature or it changed nothing — and the panel says
 * that in those words rather than choosing one of them.
 */

export function DualControlNotice({
  action,
  approvePermission,
  canCountersign,
}: {
  /** The action as a noun phrase: "Releasing the money". */
  action: string
  /** The countersigning code, named so an operator can ask for it by name. */
  approvePermission: string
  canCountersign: boolean
}) {
  return (
    <Alert tone={canCountersign ? 'info' : 'warning'}>
      {canCountersign ? <ShieldCheck aria-hidden /> : <UserCheck aria-hidden />}
      <div className="space-y-1">
        <AlertTitle>{action} is countersigned</AlertTitle>
        <AlertDescription>
          {canCountersign ? (
            <>
              Your role holds <span className="font-mono text-xs">{approvePermission}</span>, so
              this may complete as soon as you confirm it. If the branch&apos;s approval policy
              still calls for a second person — a limit by amount, most often — it is queued
              instead, and this screen says so afterwards.
            </>
          ) : (
            <>
              Your role does not hold <span className="font-mono text-xs">{approvePermission}</span>
              , so confirming this stores the request and changes nothing yet. Someone holding that
              permission has to countersign it before it takes effect. Tell the member after that
              has happened, not now.
            </>
          )}
        </AlertDescription>
      </div>
    </Alert>
  )
}

/**
 * What actually happened, for the endpoints that say so.
 *
 * `awaitingApproval: true` means the command is stored and nothing has changed, so the wording is
 * "sent for approval" and never "saved". The request id is shown because it is what somebody
 * quotes when they ring the approver.
 */
export function DualControlOutcome({
  result,
  awaitingDescription,
  appliedDescription,
}: {
  result: DualControlResult
  awaitingDescription: string
  appliedDescription: string
}) {
  if (result.awaitingApproval) {
    return (
      <Alert tone="warning">
        <UserCheck aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Sent for approval — nothing has changed yet</AlertTitle>
          <AlertDescription>
            {awaitingDescription} It takes effect when a second person countersigns it, and until
            then the round reads exactly as it did before.
            {result.approvalRequestId ? (
              <>
                {' '}
                Request <span className="font-mono text-xs">{result.approvalRequestId}</span>.
              </>
            ) : null}
          </AlertDescription>
          <Button asChild variant="secondary" size="sm">
            <Link to="/approvals" search={{ tab: 'mine' }}>
              Track it under Submitted by me
            </Link>
          </Button>
        </div>
      </Alert>
    )
  }

  return (
    <Alert tone="success">
      <CheckCircle2 aria-hidden />
      <div className="space-y-1">
        <AlertTitle>Done</AlertTitle>
        <AlertDescription>{appliedDescription}</AlertDescription>
      </div>
    </Alert>
  )
}

/**
 * What happened, for the countersigned endpoints that answer with the round.
 *
 * `applied` is the caller's own comparison of the figure the action moves — penalty outstanding
 * for a waiver, the written-off amount for a write-off, and so on. It is deliberately not
 * inferred here: only the screen that made the call knows which figure its action was supposed to
 * move.
 */
export function CountersignedOutcome({
  applied,
  approvePermission,
  appliedDescription,
  unchangedDescription,
}: {
  applied: boolean
  /** Named so an operator reading "queued" knows whose signature is being waited on. */
  approvePermission: string
  appliedDescription: string
  /** What the round would look like if this were queued — the same thing it looked like before. */
  unchangedDescription: string
}) {
  if (applied) {
    return (
      <Alert tone="success">
        <CheckCircle2 aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Done</AlertTitle>
          <AlertDescription>{appliedDescription}</AlertDescription>
        </div>
      </Alert>
    )
  }

  return (
    <Alert tone="warning">
      <HelpCircle aria-hidden />
      <div className="space-y-1">
        <AlertTitle>The round has not changed — this is almost certainly queued</AlertTitle>
        <AlertDescription>
          {unchangedDescription} This endpoint answers with the round whether it acted or queued
          the request, so the only thing that can be established from here is that nothing moved.
          Under a policy that calls for{' '}
          <span className="font-mono text-xs">{approvePermission}</span> that is what a request
          waiting for a countersignature looks like. Check{' '}
          <Link to="/approvals" search={{ tab: 'mine' }} className="underline underline-offset-2">
            Submitted by me
          </Link>{' '}
          before telling the member anything, and do not send it a second time.
        </AlertDescription>
      </div>
    </Alert>
  )
}
